import React, { useRef } from 'react'

import { FormLabel } from '@octane/spark'
import { ErrorMessage } from 'components/FormikFields/FormikDropdown/FormikDropdown.styled'
import { FieldConfigBase, FieldProps } from 'containers/FormBuilder/index'

import { ToggleButton, ToggleList, Wrapper } from './ToggleSelectField.styled'

export type ToggleSelectItem = {
    label: string
    value: string
}

interface ToggleSelectFieldConfig extends FieldConfigBase {
    label: string
    info?: React.ReactNode
    items: ToggleSelectItem[]
    prefilled?: boolean
}

export default function ToggleSelectField(
    props: FieldProps<ToggleSelectFieldConfig>,
): JSX.Element {
    const { fieldName, fieldKit, fieldConfig } = props
    const touched = fieldKit.touched[fieldName]
    const error = fieldKit.errors[fieldName]
    const value = fieldKit.values[fieldName]
    const buttonRefs = useRef<Array<HTMLButtonElement | null>>([])
    const labelId = `${fieldName}-label`

    const selectedIndex = fieldConfig.items.findIndex(
        (item) => item.value === value,
    )

    const rovingIndex = selectedIndex === -1 ? 0 : selectedIndex

    const selectItem = (index: number) => {
        fieldKit.setFieldTouched(fieldName, true)
        fieldKit.setFieldValue(fieldName, fieldConfig.items[index].value)
        buttonRefs.current[index]?.focus()
    }

    const handleKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>,
        index: number,
    ) => {
        const { items } = fieldConfig

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault()
            selectItem((index + 1) % items.length)
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault()
            selectItem((index - 1 + items.length) % items.length)
        }
    }

    return (
        <Wrapper>
            <FormLabel id={labelId} info={fieldConfig.info}>
                {fieldConfig.label}
            </FormLabel>
            <ToggleList
                id={fieldName}
                data-testid={fieldName}
                role="radiogroup"
                aria-labelledby={labelId}
            >
                {fieldConfig.items.map((item, index) => (
                    <ToggleButton
                        key={`toggle-${fieldName}-${item.value}`}
                        ref={(el) => {
                            buttonRefs.current[index] = el
                        }}
                        type="button"
                        role="radio"
                        aria-checked={value === item.value}
                        active={value === item.value}
                        disabled={fieldConfig.prefilled}
                        tabIndex={index === rovingIndex ? 0 : -1}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        onClick={() => selectItem(index)}
                    >
                        {item.label}
                    </ToggleButton>
                ))}
            </ToggleList>
            <input hidden name={fieldName} value={value ?? ''} readOnly />
            {touched && error && <ErrorMessage>{error}</ErrorMessage>}
        </Wrapper>
    )
}
